<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { TimerOrientation } from '#src/modules/timer/timerOrientationEnum'
import { TimerProgression } from '#src/modules/timer/timerProgressionEnum'
import type { TimerBarPosition } from '#src/modules/timer/interfaces/TimerBarPosition'

export interface NXGRetailTimerBarProps {
  duration: number
  transitionDelay?: number
  loadingBarColor: string
  bgColor: string
  orientation: TimerOrientation
  progressionStyle: TimerProgression
  position: TimerBarPosition
  height?: string | number
  width?: string | number
}

const {
  duration,
  transitionDelay = 0,
  loadingBarColor,
  bgColor,
  orientation,
  progressionStyle,
  position,
  height,
  width
} = defineProps<NXGRetailTimerBarProps>()

const normalizeSize = (value: string | number | undefined): string | undefined => {
  if (!value) return undefined
  return typeof value === 'number' ? `${value}px` : value
}

const startProgress = computed(() => (progressionStyle === TimerProgression.ASC ? '0' : '100%'))
const endProgress = computed(() => (progressionStyle === TimerProgression.ASC ? '100%' : '0'))
const progress = ref(startProgress.value)
const transition = ref(
  `${orientation === TimerOrientation.Horizontal ? 'width' : 'height'} ${duration}ms linear ${transitionDelay}ms`
)
let timer: number

const verticalBarStyle = computed(() => ({
  height: progress.value,
  transition: transition.value,
  background: loadingBarColor,
  ...position
}))
const horizontalBarStyle = computed(() => ({
  width: progress.value,
  transition: transition.value,
  background: loadingBarColor,
  ...position
}))
const bgBarStyle = computed(() => ({
  background: bgColor,
  ...(orientation === TimerOrientation.Vertical && height ? { height: normalizeSize(height) } : {}),
  ...(orientation === TimerOrientation.Horizontal && width ? { width: normalizeSize(width) } : {}),
  ...position
}))
const loadingBarStyle =
  orientation === TimerOrientation.Horizontal ? horizontalBarStyle : verticalBarStyle

const resetTimerAnimation = () => {
  progress.value = startProgress.value
  transition.value = 'none'
  window.clearTimeout(timer)

  timer = window.setTimeout(() => {
    progress.value = endProgress.value
    transition.value = `${orientation === TimerOrientation.Horizontal ? 'width' : 'height'} ${duration}ms linear ${transitionDelay}ms`
  }, 100)
}

onMounted(resetTimerAnimation)
onUnmounted(() => window.clearTimeout(timer))

defineExpose({ resetTimerAnimation })
</script>

<template>
  <div :class="['nxg-retail-timer-bar nxg-retail-timer-bar--background', orientation]" :style="bgBarStyle" />
  <div :class="['nxg-retail-timer-bar nxg-retail-timer-bar--loading', orientation]" :style="loadingBarStyle" />
</template>

<style scoped>
.nxg-retail-timer-bar {
  position: absolute;
  border-radius: 3px;
}

.nxg-retail-timer-bar.vertical {
  width: 6px;
}

.nxg-retail-timer-bar.horizontal {
  height: 6px;
}

.nxg-retail-timer-bar--background {
  z-index: 1;
}

.nxg-retail-timer-bar--background.vertical {
  height: 100%;
}

.nxg-retail-timer-bar--background.horizontal {
  width: 100%;
}

.nxg-retail-timer-bar--loading {
  z-index: 2;
  overflow: hidden;
}
</style>
